import plur from 'plur';
import logomarkForestAsset from '@/assets/images/logos/logomark-forest.svg';
import s3Asset from '@/assets/images/integrations/s3.svg';
import googleCloudStorageAsset from '@/assets/images/integrations/google-cloud-storage.svg';
import azureBlobStorageAsset from '@/assets/images/integrations/azure-blob-storage.svg';
import yandexAsset from '@/assets/images/integrations/yandex.svg';
import snowflakeAsset from '@/assets/images/integrations/snowflake.svg';
import bigqueryAsset from '@/assets/images/integrations/bigquery.svg';
import redshiftAsset from '@/assets/images/integrations/redshift.svg';
import segmentAsset from '@/assets/images/integrations/segment.svg';
import type { Integration } from '@/services/db/integration/entity';
import {
  ConnectionType,
  DataFlow,
  IntegrationCategory,
  IntegrationSyncEnabledStatusMessage,
  IntegrationType,
} from '../catalog/enums';
import { IntegrationIDs, IntegrationNames } from '../catalog/ids';
import { getStaticIntegrations } from '../catalog/static-integrations';
import {
  integrationConnectionType,
  integrationDataFlow,
  integrationEnabled,
  integrationIcon,
  integrationImageSrc,
  integrationPublicType,
} from '../derived';
import { mapSqlDatabaseIntegration, SqlImportDatabase, type SqlImport } from './sql-imports';
import type { SynthesizedIntegration } from './types';

const SHOW_FIRST_WEIGHT = 100;

/**
 * Backend catalog ids for data-warehouse / WhatsApp integrations that are added
 * to the list manually above (with live warehouse-sync status), so the raw
 * backend rows for them are filtered out to avoid duplicates.
 */
const MANUALLY_ADDED_BACKEND_IDS: readonly string[] = [
  'Amazon_S3',
  'Google_Cloud_Storage',
  'Azure_Blob_Storage',
  'Google_BigQuery',
  'Amazon_Redshift',
  'Snowflake',
  'MySQL',
  'PostgreSQL',
  'WhatsApp',
];

export interface IntegrationsListSources {
  /** The consolidated backend catalog ("spreadsheet") list. */
  backendIntegrations: readonly Integration[];
  /** Ad-audience credentials (one per ad network); presence drives `enabled`. */
  audienceCredentials: readonly { type: string }[];
  /** Adwords ad audiences; a non-`paused` state counts as an active sync. */
  adwordsAudiences: readonly { state?: string }[];
  /** Facebook ad audiences. */
  facebookAudiences: readonly { state?: string }[];
  /** Whether an mParticle integration is configured. */
  mparticleEnabled: boolean;
  /** Warehouse-sync status (configured storage `type` + paused), or null. */
  warehouseSync: { type: string; isPaused: boolean } | null;
  /** SQL imports grouped by database, or null until the six reads settle. */
  sqlImportsByDatabase: Partial<Record<SqlImportDatabase, readonly SqlImport[]>> | null;
  reportingWebhooksCount: number;
  forms: { facebookLeadAds: boolean; customForms: boolean; typeform: boolean; jotform: boolean; total: number };
  apiKeysCount: number;
  segmentInPresent: boolean;
  segmentOutEnabled: boolean;
  cdpOutEnabled: boolean;
  /** Ids of NativeDataOut integrations proven enabled via `data_out/:id`. */
  enabledDataOutIds: ReadonlySet<string>;
}

export interface IntegrationsListConfig {
  /** The public-directory flag — always false for the authenticated directory. */
  isPublic?: boolean;
  /** The environment's `enabled_actions` — gates the channel integrations. */
  enabledActions: readonly string[];
  isTwilioWhiteLabeled?: boolean;
  /** Whether URL-params (Google Analytics) are enabled; deferred → false in PR1. */
  urlParamsEnabled?: boolean;
  terms?: {
    customersPlural?: string;
    customerSingular?: string;
    campaignsPlural?: string;
    peoplePlural?: string;
  };
}

/** Map a raw backend catalog row into a synthesized entry via the derived helpers. */
function backendToSynthesized(
  integration: Integration,
  enabledDataOutIds: ReadonlySet<string>,
): SynthesizedIntegration {
  return {
    id: integration.id,
    name: integration.name,
    category: integration.category,
    beta: integration.beta,
    overview: integration.overview,
    description: integration.description,
    subtitle: integration.subtitle,
    homepage: integration.homepage,
    type: integration.type as IntegrationType,
    forceOrder: 0,
    imageSrc: integrationImageSrc(integration),
    icon: integrationIcon(integration),
    connectionType: integrationConnectionType(integration),
    dataFlow: integrationDataFlow(integration),
    publicType: integrationPublicType(integration),
    enabled: integrationEnabled(integration, enabledDataOutIds),
    is_premium: integration.is_premium,
    direction: integration.direction,
    connection_type: integration.connection_type,
    image_url: integration.image_url,
    cta_url: integration.cta_url,
    cta_label: integration.cta_label,
    use_cases: integration.use_cases,
    details_page_description: integration.details_page_description,
    detailsPageTitle: integration.details_page_title,
    settings: integration.settings,
  };
}

type WarehouseSyncStatus = { type: string; isPaused: boolean } | null;

/** Build a warehouse-sync directory card (S3 / GCS / Azure / Yandex). */
function warehouseCard(opts: {
  id: string;
  /** The warehouse-sync storage token this card is enabled by. */
  storageType: string;
  imageSrc: string;
  name: string;
  overview: string;
  dynamicSegment: string;
  warehouseSync: WarehouseSyncStatus;
  /** Yandex always reports "exporting" rather than switching on paused. */
  alwaysExporting?: boolean;
}): SynthesizedIntegration {
  const enabled = opts.warehouseSync?.type === opts.storageType;
  const paused = enabled && (opts.warehouseSync?.isPaused ?? false);
  const enabledStatus =
    opts.alwaysExporting !== true && paused
      ? IntegrationSyncEnabledStatusMessage.paused
      : IntegrationSyncEnabledStatusMessage.exporting;
  return {
    id: opts.id,
    forceOrder: 0,
    imageSrc: opts.imageSrc,
    name: opts.name,
    overview: opts.overview,
    enabledStatus,
    category: IntegrationCategory.databases,
    enabled,
    paused,
    beta: false,
    type: IntegrationType.native,
    publicType: IntegrationType.existing,
    path: 'integrations.warehouse.type',
    dynamicSegment: opts.dynamicSegment,
    dataFlow: DataFlow.out,
    premiumIntegration: true,
  };
}

/** Build the pair of ad-network directory cards (adwords+youtube / facebook+instagram). */
function adNetworkCards(opts: {
  primary: SynthesizedIntegration;
  secondary: SynthesizedIntegration;
  credentialPresent: boolean;
  audiences: readonly { state?: string }[];
  dynamicSegment: string;
  isPublic: boolean;
}): SynthesizedIntegration[] {
  const activeAudiences = opts.audiences.filter((audience) => audience.state !== 'paused').length;
  const enabledStatus =
    activeAudiences > 0 ? `Syncing ${activeAudiences} ${plur('segment', activeAudiences)}` : 'No active syncs';
  const path = opts.credentialPresent ? 'integrations.audiences.type.settings' : 'integrations.audiences.type.setup';
  return [
    {
      ...opts.primary,
      dataFlow: DataFlow.out,
      enabled: !opts.isPublic && opts.credentialPresent,
      enabledStatus,
      dynamicSegment: opts.dynamicSegment,
      path,
    },
    {
      ...opts.secondary,
      dataFlow: DataFlow.out,
      enabled: opts.credentialPresent,
      enabledStatus,
      dynamicSegment: opts.dynamicSegment,
      path,
    },
  ];
}

function decoupleSqlIntegrations(
  sqlImportsByDatabase: IntegrationsListSources['sqlImportsByDatabase'],
  peoplePlural: string,
): SynthesizedIntegration[] {
  if (sqlImportsByDatabase == null) {
    return [];
  }
  // One card per database, always six. A database with no imports gets an empty
  // array (enabled false); rows under an unrecognized `database_type` key are
  // ignored (only the six known databases are read).
  return Object.values(SqlImportDatabase).map((database) =>
    mapSqlDatabaseIntegration(sqlImportsByDatabase[database] ?? [], database, peoplePlural),
  );
}

/**
 * Merge the static base catalog, the backend catalog list, and live fan-out
 * status into the flat list the directory/overview pages render. Pure: all
 * inputs are explicit, no service or store access. Entry order is meaningful
 * only as input to `sortIntegrations`, which re-sorts by `['forceOrder', 'name']`.
 */
// fallow-ignore-next-line complexity -- a flat catalog assembly of ~20 literal entries; the length is the entry list itself. Repetitive shapes are already extracted (warehouseCard / adNetworkCards / decoupleSqlIntegrations / backendToSynthesized). Further splitting would fragment the readable one-entry-per-block layout.
export function buildIntegrationsList(
  sources: IntegrationsListSources,
  config: IntegrationsListConfig,
): SynthesizedIntegration[] {
  const isPublic = config.isPublic ?? false;
  const enabledActions = config.enabledActions;
  const isTwilioWhiteLabeled = config.isTwilioWhiteLabeled ?? false;
  const urlParamsEnabled = config.urlParamsEnabled ?? false;
  const customersPlural = config.terms?.customersPlural ?? 'customers';
  const customerSingular = config.terms?.customerSingular ?? 'customer';
  const campaignsPlural = config.terms?.campaignsPlural ?? 'campaigns';
  const peoplePlural = config.terms?.peoplePlural ?? 'people';

  // Taxonomy-aware base metadata (segment / CDP descriptions embed the audience
  // noun); the token-less default snapshot is reused for entries that do not
  // re-template their copy.
  const metadata = getStaticIntegrations({ customersPlural, customerSingular });
  const INTEGRATIONS_METADATA = getStaticIntegrations();

  const adwordsCredentials = sources.audienceCredentials.find((cred) => cred.type === 'adwords');
  const facebookCredentials = sources.audienceCredentials.find((cred) => cred.type === 'facebook');
  const warehouseSync = sources.warehouseSync;
  const reportingWebhooksTotal = sources.reportingWebhooksCount;
  const forms = sources.forms;
  const apiKeysTotal = sources.apiKeysCount;

  const entries: (SynthesizedIntegration | null)[] = [
    {
      id: IntegrationIDs.customerioAPI,
      forceOrder: -1,
      imageSrc: logomarkForestAsset,
      name: IntegrationNames.customerioAPI,
      overview: 'Bring data into Customer.io with Ruby, JavaScript, Go, or other languages',
      enabledStatus: `${apiKeysTotal} API ${plur('key', apiKeysTotal)} available`,
      category: IntegrationCategory.api,
      enabled: true,
      beta: false,
      path: 'integrations.customerio',
      type: IntegrationType.native,
      publicType: IntegrationType.existing,
      dataFlow: DataFlow.in,
      highlight: true,
      weight: 0,
      total: apiKeysTotal,
    },
    {
      id: IntegrationIDs.email,
      forceOrder: 0,
      icon: 'email',
      name: IntegrationNames.email,
      overview: 'Send email in Customer.io',
      enabled: true,
      beta: false,
      path: 'actions.email.sending_domains',
      type: IntegrationType.native,
      publicType: IntegrationType.existing,
      subtitle: 'Channel',
      dataFlow: DataFlow.channel,
    },
    {
      id: IntegrationIDs.reportingWebhooks,
      forceOrder: 0,
      icon: 'webhook',
      name: IntegrationNames.reportingWebhooks,
      overview: 'Send data from Customer.io to any server in real time.',
      enabledStatus: `Sending customer and email events to ${reportingWebhooksTotal} ${plur('URL', 'URLs', reportingWebhooksTotal)}`,
      category: IntegrationCategory.dataManagement,
      enabled: reportingWebhooksTotal > 0,
      beta: false,
      type: IntegrationType.native,
      publicType: IntegrationType.existing,
      connectionType: ConnectionType.webhook,
      path: 'integrations.reporting-webhooks.new',
      dynamicSegment: undefined,
      dataFlow: DataFlow.out,
      highlight: true,
      total: reportingWebhooksTotal,
    },
    {
      id: IntegrationIDs.webhooks,
      forceOrder: 0,
      icon: 'webhook',
      name: IntegrationNames.webhooks,
      overview:
        'Use webhooks as triggers to bring data into Customer.io, or send data out of Customer.io in real time.',
      category: IntegrationCategory.MessagingChannelTypes,
      enabled: false,
      beta: false,
      path: 'https://docs.customer.io/webhooks-action',
      external: true,
      type: IntegrationType.native,
      publicType: IntegrationType.existing,
      connectionType: ConnectionType.webhook,
      dataFlow: DataFlow.in,
    },
    {
      id: IntegrationIDs.mobile,
      forceOrder: 0,
      icon: 'mobile',
      name: IntegrationNames.mobile,
      overview: 'Send mobile push messages in Customer.io',
      dataFlow: DataFlow.channel,
      enabled: !isPublic && enabledActions.includes('push'),
      beta: false,
      type: IntegrationType.native,
      publicType: IntegrationType.existing,
      path: 'actions.push',
      subtitle: 'Channel',
    },
    {
      id: IntegrationIDs.inApp,
      icon: 'in-app',
      name: IntegrationNames.inApp,
      overview: "Engage users when they're active in your app",
      enabled: !isPublic && enabledActions.includes('in_app'),
      beta: false,
      type: IntegrationType.native,
      publicType: IntegrationType.existing,
      path: 'actions.in_app',
      subtitle: 'Channel',
      dataFlow: DataFlow.channel,
    },
    warehouseCard({
      id: IntegrationIDs.amazonS3,
      storageType: 's3',
      imageSrc: s3Asset,
      name: IntegrationNames.amazonS3,
      overview: 'Send data from Customer.io to Amazon Simple Storage Service / S3.',
      dynamicSegment: 's3',
      warehouseSync,
    }),
    warehouseCard({
      id: IntegrationIDs.gcs,
      storageType: 'gcs',
      imageSrc: googleCloudStorageAsset,
      name: IntegrationNames.gcs,
      overview: 'Send data from Customer.io to Google Cloud Storage / GCS.',
      dynamicSegment: IntegrationIDs.gcs,
      warehouseSync,
    }),
    warehouseCard({
      id: IntegrationIDs.azureBlob,
      storageType: 'azure_blob',
      imageSrc: azureBlobStorageAsset,
      name: IntegrationNames.azureBlob,
      overview: 'Send data from Customer.io to Azure Blob Storage.',
      dynamicSegment: IntegrationIDs.azureBlob,
      warehouseSync,
    }),
    warehouseCard({
      id: IntegrationIDs.yandex,
      storageType: 'yandex',
      imageSrc: yandexAsset,
      name: IntegrationNames.yandex,
      overview: 'Publish Customer.io data to Yandex',
      dynamicSegment: IntegrationIDs.yandex,
      warehouseSync,
      alwaysExporting: true,
    }),
    {
      id: IntegrationIDs.snowflakeOut,
      forceOrder: 0,
      imageSrc: snowflakeAsset,
      name: IntegrationNames.snowflakeOut,
      subtitle: 'Data out',
      overview: 'Send data from Customer.io to Snowflake.',
      category: IntegrationCategory.databases,
      enabled: false,
      beta: false,
      type: IntegrationType.native,
      publicType: IntegrationType.existing,
      path: 'integrations.warehouse.snowflake',
      dataFlow: DataFlow.out,
      premiumIntegration: true,
    },
    {
      id: IntegrationIDs.bigQueryOut,
      forceOrder: 0,
      imageSrc: bigqueryAsset,
      name: IntegrationNames.bigQueryOut,
      subtitle: 'Data out',
      overview: 'Send data from Customer.io to Google BigQuery.',
      category: IntegrationCategory.databases,
      enabled: false,
      beta: false,
      type: IntegrationType.native,
      publicType: IntegrationType.existing,
      path: 'integrations.warehouse.bigquery',
      dataFlow: DataFlow.out,
      premiumIntegration: true,
    },
    {
      id: IntegrationIDs.redshiftOut,
      forceOrder: 0,
      imageSrc: redshiftAsset,
      name: IntegrationNames.redshiftOut,
      subtitle: 'Data out',
      overview: 'Send data from Customer.io to Amazon Redshift.',
      category: IntegrationCategory.databases,
      enabled: false,
      beta: false,
      type: IntegrationType.native,
      publicType: IntegrationType.existing,
      path: 'integrations.warehouse.redshift',
      dataFlow: DataFlow.out,
      premiumIntegration: true,
    },
    ...decoupleSqlIntegrations(sources.sqlImportsByDatabase, peoplePlural),
    ...adNetworkCards({
      primary: INTEGRATIONS_METADATA.adwords,
      secondary: INTEGRATIONS_METADATA.youtube,
      credentialPresent: adwordsCredentials != null,
      audiences: sources.adwordsAudiences,
      dynamicSegment: 'adwords',
      isPublic,
    }),
    {
      ...INTEGRATIONS_METADATA.googleAnalytics,
      enabled: urlParamsEnabled,
      enabledStatus: 'URL parameters enabled',
      path: 'actions.url_params',
      dynamicSegment: undefined,
      parsedUseCases: [`Automatically append UTM parameters to links in Customer.io ${campaignsPlural}`],
    },
    ...adNetworkCards({
      primary: INTEGRATIONS_METADATA.facebook,
      secondary: INTEGRATIONS_METADATA.instagram,
      credentialPresent: facebookCredentials != null,
      audiences: sources.facebookAudiences,
      dynamicSegment: 'facebook',
      isPublic,
    }),
    {
      id: IntegrationIDs.customForms,
      forceOrder: 0,
      icon: 'form',
      name: IntegrationNames.customForms,
      overview: 'Connect your third-party forms to Customer.io',
      enabledStatus: `${forms.total} ${plur('form', forms.total)} connected`,
      category: IntegrationCategory.leadGeneration,
      enabled: forms.total > 0,
      beta: false,
      type: IntegrationType.native,
      publicType: IntegrationType.existing,
      path: 'integrations.forms.type',
      dynamicSegment: 'custom',
      highlight: true,
      total: forms.total,
      dataFlow: DataFlow.in,
    },
    {
      ...INTEGRATIONS_METADATA.typeform,
      enabled: forms.typeform,
      path: 'integrations.forms.type',
      overview: `Create profiles and trigger ${campaignsPlural} from Typeform`,
      parsedUseCases: [
        `Create Customer.io profiles and trigger ${campaignsPlural} when a new Typeform response is recorded.`,
      ],
    },
    {
      ...INTEGRATIONS_METADATA.jotform,
      enabled: forms.jotform,
      path: 'integrations.forms.type',
      overview: `Create profiles and trigger ${campaignsPlural} from Jotform`,
    },
    {
      ...INTEGRATIONS_METADATA.facebookLeadAds,
      enabled: !isPublic && forms.facebookLeadAds,
      path: 'integrations.forms.type',
      parsedUseCases: [
        'Add Customer.io profiles from Facebook Lead Ad submissions',
        `Trigger Customer.io ${campaignsPlural} from Facebook Lead Ad submissions`,
      ],
    },
    !isPublic && sources.segmentInPresent
      ? {
          id: IntegrationIDs.segmentIn,
          forceOrder: 0,
          imageSrc: segmentAsset,
          name: IntegrationNames.segmentIn,
          subtitle: 'Destination',
          overview: 'Push your Segment.com data into Customer.io',
          category: IntegrationCategory.dataManagement,
          description: 'Segment allows you to unify customer data and analytics in one place',
          parsedUseCases: ['Push profiles, events, and attributes from Segment to Customer.io'],
          beta: false,
          type: IntegrationType.native,
          publicType: IntegrationType.existing,
          homepage: 'https://segment.io',
          enabled: true,
          path: 'integrations.segment_in',
          dynamicSegment: undefined,
          legacy: true,
          dataFlow: DataFlow.in,
        }
      : null,
    {
      ...metadata.segmentOut,
      enabled: !isPublic && sources.segmentOutEnabled,
      enabledStatus: 'Sending data',
      path: 'integrations.segment_out',
      dynamicSegment: undefined,
    },
    ...(!isPublic
      ? [
          {
            ...metadata.cdpOut,
            weight: SHOW_FIRST_WEIGHT,
            id: IntegrationIDs.cdpIn,
            dataFlow: DataFlow.in,
            enabled: !isPublic && sources.cdpOutEnabled,
            enabledStatus: 'Receiving data',
            path: 'integrations.cdp_in',
            dynamicSegment: undefined,
          },
          {
            ...metadata.cdpOut,
            weight: SHOW_FIRST_WEIGHT,
            enabled: !isPublic && sources.cdpOutEnabled,
            enabledStatus: 'Sending data',
            path: 'integrations.cdp_out',
            dynamicSegment: undefined,
          },
        ]
      : []),
    {
      ...INTEGRATIONS_METADATA.mparticleOut,
      enabled: sources.mparticleEnabled,
      enabledStatus: 'Sending email events',
      path: 'integrations.mparticle_out',
      dynamicSegment: undefined,
    },
    {
      ...INTEGRATIONS_METADATA.line,
      description: `LINE is a messaging app that allows you to send and receive messages with your ${customersPlural}.`,
      enabled: !isPublic && enabledActions.includes('line'),
      path: 'actions.line',
      dynamicSegment: undefined,
      parsedUseCases: [`Send LINE messages in Customer.io ${campaignsPlural}`],
    },
    {
      ...INTEGRATIONS_METADATA.twilio,
      description: `Customer.io's SMS Action allows you to easily communicate with your ${customersPlural} through Customer.io.`,
      enabled: !isPublic && enabledActions.includes('twilio'),
      path: 'actions.twilio',
      dynamicSegment: undefined,
      name: isTwilioWhiteLabeled ? 'SMS' : INTEGRATIONS_METADATA.twilio.name,
      icon: isTwilioWhiteLabeled ? 'sms' : undefined,
      imageSrc: isTwilioWhiteLabeled ? '' : INTEGRATIONS_METADATA.twilio.imageSrc,
      beta: isTwilioWhiteLabeled ? true : INTEGRATIONS_METADATA.twilio.beta,
      parsedUseCases: [`Send SMS messages in Customer.io ${campaignsPlural}`],
    },
    {
      ...INTEGRATIONS_METADATA.slack,
      enabled: !isPublic && enabledActions.includes('slack'),
      path: 'actions.slack',
      dynamicSegment: undefined,
      subtitle: 'Channel',
      parsedUseCases: [`Send Slack messages to individuals or channels through Customer.io ${campaignsPlural}`],
    },
    {
      id: IntegrationIDs.WhatsApp,
      forceOrder: 0,
      imageSrc: INTEGRATIONS_METADATA.WhatsApp.imageSrc,
      name: IntegrationNames.WhatsApp,
      overview: 'Send WhatsApp messages in Customer.io.',
      description: 'Send WhatsApp messages to your customers through Customer.io.',
      parsedUseCases: [`Send WhatsApp messages in Customer.io ${campaignsPlural}`],
      enabled: !isPublic && enabledActions.includes('whatsapp'),
      beta: false,
      type: IntegrationType.native,
      publicType: IntegrationType.existing,
      path: 'actions.whatsapp',
      dynamicSegment: undefined,
      homepage: 'https://www.whatsapp.com',
      dataFlow: DataFlow.channel,
    },
    {
      ...INTEGRATIONS_METADATA.WhatsApp,
      id: 'WhatsApp_Twilio',
      name: 'WhatsApp (Twilio)',
      parsedUseCases: [`Send WhatsApp messages in Customer.io ${campaignsPlural}`],
    },
    ...sources.backendIntegrations
      .filter((i) => !MANUALLY_ADDED_BACKEND_IDS.includes(i.id))
      .map((i) => backendToSynthesized(i, sources.enabledDataOutIds)),
  ];

  return entries.filter((integration): integration is SynthesizedIntegration => integration != null);
}
